import { ConfigModule } from '@built/config-utility';

export function getConfig(key: string, defaultValue?: string): string {
  if (process.env.BABEL_ENV === 'test') {
    return getTestConfig(key, defaultValue);
  }

  switch (key) {
    case 'REACT_APP_BUILT_NATIV_URL':
      const dealUrlEnv =
        ConfigModule.getLendingConfig().env !== 'production'
          ? `-${ConfigModule.getLendingConfig().env}`
          : '';
      return `https://deal${dealUrlEnv}.getbuilt.com`;
    case 'REACT_APP_PROCESS_ENV':
      return ConfigModule.getLendingConfig().env ?? defaultValue ?? '';
    case 'REACT_APP_BUILT_API_URL':
      return ConfigModule.getLendingConfig().bapi.uri;
    case 'REACT_APP_BUILT_PRODUCT_API_URL':
      return ConfigModule.getLendingConfig().apiUrl;
    case 'REACT_APP_BUILT_CONSOLE_URL':
      return ConfigModule.getLendingConfig().consoleURL;
    case 'REACT_APP_BUILT_LENDING_URL':
      return ConfigModule.getLendingConfig().lendingURL;
    case 'REACT_APP_BUILT_CONSTRUCTION_URL':
      return ConfigModule.getConstructionConfig().constructionURL;
    case 'REACT_APP_BUILT_OWNERS_URL':
      return `${
        ConfigModule.getConstructionConfig().constructionURL
      }/financial-relationships/#/`;
    case 'REACT_APP_BUILT_OWNERS_BORROWER_URL':
      return ConfigModule.getConstructionConfig().ownersURL;
    case 'REACT_APP_BUILT_VENDORS_URL':
      return ConfigModule.getConstructionConfig().vendorURL;
    case 'REACT_APP_DEFAULT_REDIRECT_APP':
      return 'cla';
    case 'REACT_APP_BUILT_COOKIE_DOMAIN':
      return ConfigModule.getLendingConfig().cookies.domain;
    case 'REACT_APP_BUILT_COOKIE_EXPIRE_DAYS':
      return '30';
    case 'REACT_APP_BUILT_COOKIE_PATH':
      return '/';
    case 'REACT_APP_BUILT_COOKIE_SECURE':
      return ConfigModule.getLendingConfig().cookies.secure ? 'true' : 'false';
    case 'REACT_APP_USE_MOCK_COGNITO':
      return ConfigModule.getLendingConfig().cognito.isMocked
        ? 'true'
        : 'false';
    case 'REACT_APP_USER_POOL_ID':
      return ConfigModule.getConstructionConfig().cognito.userPoolId;
    case 'REACT_APP_USER_POOL_DOMAIN_PREFIX':
      return `built-${ConfigModule.getConstructionConfig().env}`;
    case 'REACT_APP_CLIENT_ID':
      return ConfigModule.getConstructionConfig().cognito.userPoolWebClientId;
    case 'REACT_APP_REGION':
      return ConfigModule.getConstructionConfig().cognito.region;
    case 'BAPI_ID':
      return ConfigModule.getLendingConfig().bapiId;
    case 'BAPI_XID':
      return ConfigModule.getLendingConfig().bapiXId;
    default:
      // anything still using global.config / tests
      let value;
      if (global.config && key in global.config) {
        value = global.config[key];
      }

      if (window.config && key in window.config) {
        value = window.config[key];
      }

      if (value === undefined) {
        if (defaultValue === undefined) {
          throw new Error(`Required configuration "${key}" not found`);
        }
        return defaultValue;
      }
      return value;
  }
}

// NOTE: https://getbuilt.atlassian.net/wiki/spaces/TZC/pages/4006576237/App+claim+product+mapping
export const ClaimsToProducts: Record<string, string> = {
  b4c: 'b4c',
  b4l: 'cla',
  'b4l:cre-lender': 'cla'
};

export function mapClaimToProduct(claim: string): string {
  return ClaimsToProducts[claim] || 'cla';
}

export function getTestConfig(key: string, defaultValue?: string): string {
  let value: undefined | string;

  let config = {
    REACT_APP_BUILT_API_URL: 'http://api.getbuilt.local/v1.3',
    REACT_APP_BUILT_PRODUCT_API_URL: 'http://api.getbuilt.local/v1.3',
    REACT_APP_BUILT_CONSOLE_URL: 'https://console-ops.getbuilt.com',
    REACT_APP_BUILT_LENDING_URL: 'https://app-ops.getbuilt.com',
    REACT_APP_BUILT_CONSTRUCTION_URL: 'http://construction.getbuilt.local',
    REACT_APP_CLIENT_ID: 'fakeClientId',
    REACT_APP_BUILT_COOKIE_DOMAIN: 'localhost',
    REACT_APP_BUILT_COOKIE_PATH: '/',
    REACT_APP_BUILT_COOKIE_SECURE: 'false',
    REACT_APP_REGION: 'us-east-1',
    REACT_APP_USE_MOCK_COGNITO: 'false',
    REACT_APP_USER_POOL_DOMAIN_PREFIX: 'foo',
    REACT_APP_USER_POOL_ID: 'fake-userPoolId',
    REACT_APP_INTERCOM_APP_ID: 'fakeIntercomAppId',
    REACT_APP_SNOWPLOW_ENABLED: 'true',
    REACT_APP_DEFAULT_REDIRECT_APP: 'cla',
    REACT_APP_BUILT_OWNERS_URL: 'http://construction.getbuilt.local'
  };

  if (global.config && key in global.config) {
    value = global.config[key];
  } else if (key in config) {
    value = config[key];
  }

  if (value === undefined) {
    if (defaultValue === undefined) {
      throw new Error(`Required configuration "${key}" not found`);
    }
    return defaultValue;
  }

  return value;
}

export interface WindowConfig {
  REACT_APP_BUILT_API_URL?: string;
  REACT_APP_BUILT_CONSOLE_URL?: string;
  REACT_APP_BUILT_COOKIE_DOMAIN?: string;
  REACT_APP_BUILT_COOKIE_PATH?: string;
  REACT_APP_BUILT_COOKIE_SECURE?: string;
  REACT_APP_BUILT_COOKIE_EXPIRE_DAYS?: string;
  REACT_APP_USE_MOCK_COGNITO?: string;
  REACT_APP_USER_POOL_ID?: string;
  REACT_APP_USER_POOL_DOMAIN_PREFIX?: string;
  REACT_APP_CLIENT_ID?: string;
  REACT_APP_REGION?: string;
  REACT_APP_DEFAULT_REDIRECT_APP?: string;
  REACT_APP_SESSION_STORAGE?: string;
}
